import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";

import Grid from "@mui/material/Grid";

import ImageCard from "../../components/ImageCard";
import API from "../../api";
import { updateBeefCuts } from "../../actions/Data";
import { getTranslation } from "../../utils/translate";
import ListPage from "../../layouts/ListPage";
import useCountInJunction from "../../common/hooks/useCountInJunction";
import LibraryActions from "../../components/shared/LibraryActions";
import LazyLoad from "react-lazyload";
import { getBeefProductOverride } from "../../utils/overrides";
import { languages } from "../../utils/translate";
import useSWR from "swr";
import { pageNumber, pageSize } from "../../utils/pagination";

const BeefCutsList = () => {
  const retailer = useSelector((state) => state.retailer);
  const beefCutsStore = useSelector((state) => state.beefCuts);

  const [language, setLanguage] = useState(languages[0]);
  const [page, setPage] = useState(pageNumber);
  const [libraryStatusOverrides, setLibraryStatusOverrides] = useState({});

  const dispatch = useDispatch();

  const { data: pageData, isLoading: pageDataLoading } = useSWR({
    url: `beef_product?meta=*`,
    fields: `id,date_created,parent_group,owner,svg_icon,image,retailers.retailer_id,translations.*`,
    deep: {
      beef_product_overrides: {
        _filter: {
          retailer: {
            _eq: retailer.id,
          },
        },
      },
    },
    sort: "-date_created",
    limit: pageSize,
    page,
  });

  const beefCutsCount = useCountInJunction(
    "beef_product_retailer",
    {
      aggregate: {
        count: "*",
      },
      filter: {
        _and: [{ retailer_id: { _eq: retailer.id } }],
      },
    },
    pageDataLoading
  );

  const visibleBeefCutIds = pageData?.data?.map((item) => item?.id).filter(Boolean) || [];

  const { data: pageLibraryStatuses } = useSWR(
    visibleBeefCutIds.length > 0
      ? {
          url: `/beef_product_retailer`,
          fields: "beef_product_id",
          filter: {
            _and: [
              {
                retailer_id: {
                  _eq: retailer.id,
                },
              },
              {
                beef_product_id: {
                  _in: visibleBeefCutIds,
                },
              },
            ],
          },
          limit: -1,
        }
      : null,
    {
      revalidateOnFocus: false,
    }
  );

  const pageLibraryStatusMap = useMemo(() => {
    const statusMap = {};

    (pageLibraryStatuses || []).forEach((item) => {
      const beefProductId =
        typeof item?.beef_product_id === "object"
          ? item?.beef_product_id?.id
          : item?.beef_product_id;

      if (beefProductId) {
        statusMap[beefProductId] = true;
      }
    });

    return statusMap;
  }, [pageLibraryStatuses]);

  useEffect(() => {
    setLibraryStatusOverrides({});
  }, [page]);

  const summaryValues = [
    {
      label: "Total",
      value: beefCutsStore.meta.total_count,
    },
    {
      label: "Collected",
      value: beefCutsCount || "-",
    },
  ];

  const makeGetBeefCutsRequest = async () => {
    return await API.getListProducts({
      limit: 100,
      deep: {
        beef_product_overrides: {
          _filter: {
            retailer: {
              _eq: retailer.id,
            },
          },
        },
      },
      filter: {
        _and: [],
      },
    });
  };

  useEffect(() => {
    fetchBeefCuts();
  }, []);

  const fetchBeefCuts = async () => {
    try {
      const listBeefCuts = await makeGetBeefCutsRequest();

      dispatch(updateBeefCuts(listBeefCuts));
    } catch (error) {
      // Error silently handled - beef cuts will be empty
    }
  };

  return (
    <ListPage
      title={"Beef Cuts"}
      description={"View and manage beef cuts."}
      summaries={summaryValues}
      searchOnly={["BEEF_CUTS"]}
      language={language}
      setLanguage={setLanguage}
      defaultPayload={{
        url: `beef_product?meta=*`,
        fields: ["*", "translations.*"],
        filter: {
          _and: [
            {
              _or: [
                {
                  retailers: {
                    retailer_id: {
                      _eq: retailer.id,
                    },
                  },
                },
              ],
            },
          ],
        },
        sort: "-date_created",
        limit: -1,
      }}
      defaultResponseHandler={(items) => {
        return (
          items?.data?.map((item) => {
            const beefOverride = getBeefProductOverride(item, retailer, {
              code: language.code,
            });
            const translatedBeefOverride = getTranslation(beefOverride, {
              key: "languages_code",
              code: language.code,
            });

            const translatedBeefCut = getTranslation(
              item,
              { key: "languages_id", code: language.code },
              "translations"
            );
            const imageUrl = `${process.env.REACT_APP_API_URL}/assets/${item?.image}`;

            return {
              id: item?.id,
              title: translatedBeefOverride?.name || translatedBeefCut.name,
              description: translatedBeefCut.description,
              url: `/beef-cuts/${item?.id}`,
              data: item,
              imageUrl: imageUrl,
            };
          }) || []
        );
      }}
      searchStorageKey={"beef-cuts"}
      breadcrumbsLinks={[
        {
          url: "/recipes-menu",
          content: "Content Library",
        },
      ]}
      pageNumber={page}
      setPageNumber={setPage}
      numberOfPages={Math.ceil(pageData?.meta?.total_count / pageSize)}
      loading={pageDataLoading}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          {pageData?.data?.map((beefCut) => (
            <Grid key={beefCut.id} item xs={6} sm={6} md={4} lg={3} xl={2}>
              <Box
                sx={{
                  height: "100%",
                }}
              >
                <LazyLoad height="100%">
                  <ImageCard
                    url={`/beef-cuts/${beefCut.id}`}
                    item={{
                      title:
                        getTranslation(
                          beefCut,
                          { key: "languages_id", code: language.code },
                          "translations"
                        )?.name || " ",
                      description:
                        getTranslation(
                          beefCut,
                          { key: "languages_id", code: language.code },
                          "translations"
                        )?.description || " ",
                      url: beefCut.image
                        ? `${process.env.REACT_APP_API_URL}/assets/${beefCut.image}`
                        : "",
                    }}
                    footerAction={
                      <LibraryActions
                        id={beefCut.id}
                        junctionTable="beef_product_retailer"
                        junctionPayload={{
                          beef_product_id: beefCut.id,
                          retailer_id: retailer.id,
                        }}
                        inLibraryStatus={
                          typeof libraryStatusOverrides[beefCut.id] === "boolean"
                            ? libraryStatusOverrides[beefCut.id]
                            : typeof pageLibraryStatuses !== "undefined"
                            ? !!pageLibraryStatusMap[beefCut.id]
                            : undefined
                        }
                        onLibraryStatusChange={(status) => {
                          setLibraryStatusOverrides((current) => ({
                            ...current,
                            [beefCut.id]: status,
                          }));
                        }}
                      />
                    }
                    imageBackground="#fff"
                    imageFit="contain"
                  />
                </LazyLoad>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </ListPage>
  );
};

export default BeefCutsList;
